import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /**
   * Structured content rendered under the description, for confirmations that
   * commit the user to figures rather than to a single fact. Kept OUTSIDE
   * DialogDescription: that renders a <p>, so a table or a callout nested in
   * it would be invalid markup. The description stays required and stays the
   * accessible summary — this is an addition to it, never a replacement.
   */
  body?: ReactNode;
  confirmText: string;
  cancelText?: string;
  loadingText?: string;
  variant?: "default" | "destructive";
  /**
   * Blocks confirming while the dialog's own body is incomplete.
   *
   * Separate from `isLoading`, which blocks because something is in flight and
   * shows a spinner. This one means "you have not finished filling this in",
   * so it must NOT look busy. Used where the dialog collects something before
   * it can act — the award dialog gathers a delivery address when the request
   * has none.
   */
  confirmDisabled?: boolean;
  isLoading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmText,
  cancelText = "Cancel",
  loadingText,
  variant = "default",
  confirmDisabled = false,
  isLoading = false,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps): JSX.Element {
  const handleCancel = (): void => {
    if (isLoading) return;
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = async (): Promise<void> => {
    await onConfirm();
  };

  const filterOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen && isLoading) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={filterOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={variant}
            onClick={handleConfirm}
            disabled={isLoading || confirmDisabled}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingText ?? confirmText}
              </>
            ) : (
              confirmText
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
